/**
 * BucketChip (PHASE1 §7.4). A compact pill showing a bucket's icon, colour and
 * name. In the inbox grid an `index` (0-based) renders a `1–9` keyboard badge.
 * Clickable and selectable for the assign grid.
 */
import type { BucketOut } from '../../lib/types.ts';

export type BucketChipSize = 'sm' | 'md' | 'lg';

export interface BucketChipProps {
  bucket?: BucketOut | null;
  size?: BucketChipSize;
  onClick?: () => void;
  selected?: boolean;
  /** 0-based position in the inbox grid → renders a `index+1` badge (1–9). */
  index?: number;
  className?: string;
}

const SIZE: Record<BucketChipSize, string> = {
  sm: 'px-2 py-1 text-xs gap-1',
  md: 'px-3 py-1.5 text-sm gap-1.5',
  lg: 'px-4 py-3 text-sm gap-2',
};

export function BucketChip({
  bucket,
  size = 'md',
  onClick,
  selected,
  index,
  className,
}: BucketChipProps) {
  const label = bucket?.name ?? 'Nicht kategorisiert';
  const color = bucket?.color ?? undefined;
  const showBadge = typeof index === 'number' && index >= 0 && index < 9;

  const base = `inline-flex items-center rounded-full border font-medium transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:focus-visible:outline-sky-500 ${SIZE[size]}`;
  const state = selected
    ? 'border-sky-600 bg-sky-50 text-sky-900 dark:border-sky-500 dark:bg-sky-950 dark:text-sky-100'
    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60';

  const content = (
    <>
      {color ? (
        <span
          aria-hidden
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
      ) : null}
      {bucket?.icon ? <span aria-hidden>{bucket.icon}</span> : null}
      <span className="truncate">{label}</span>
      {showBadge ? (
        <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded bg-slate-200 px-1 text-[10px] font-semibold tabular-nums text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          {(index ?? 0) + 1}
        </span>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={`${base} ${state} ${className ?? ''}`}
      >
        {content}
      </button>
    );
  }
  return <span className={`${base} ${state} ${className ?? ''}`}>{content}</span>;
}
